import { ReactNode } from "react";
import { Calendar, Button } from "@jourfx/ui";
import { Loader2 } from "lucide-react";

export interface TimeSlot {
  time: string;
  timestamp: number;
  available: boolean;
}

interface DateTimeSlotPickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  slots: TimeSlot[];
  isLoading?: boolean;
  selectedSlot: number | null;
  onSlotSelect: (timestamp: number) => void;
  onConfirm: () => void;
  confirmLabel: string;
  busyLabel: string;
  emptyLabel: string;
  disableDate?: (date: Date) => boolean;
  showCalendarAvailability?: boolean;
  className?: string;
  slotGridClassName?: string;
  dateLabelRenderer?: (date: Date) => ReactNode;
}

function normalizeToDayStart(date: Date) {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
}

function isBeforeToday(date: Date) {
  const now = new Date();
  const today = normalizeToDayStart(now);
  return normalizeToDayStart(date).getTime() < today.getTime();
}

export function DateTimeSlotPicker({
  selectedDate,
  onDateChange,
  slots,
  isLoading,
  selectedSlot,
  onSlotSelect,
  onConfirm,
  confirmLabel,
  busyLabel,
  emptyLabel,
  disableDate,
  showCalendarAvailability = false,
  className,
  slotGridClassName,
  dateLabelRenderer,
}: DateTimeSlotPickerProps) {
  const availableSlots = slots.filter((slot) => slot.available);
  const selectedDay = normalizeToDayStart(selectedDate);

  return (
    <div className={className}>
      <div className="rounded-xl border border-[var(--stone-200)] p-4 bg-white">
        <div className="space-y-3">
          <div className="text-sm font-semibold uppercase tracking-wide text-[var(--stone-500)]">Termin auswählen</div>
          <Calendar
            mode="single"
            required
            selected={selectedDay}
            onSelect={(date) => {
              if (!date) return;
              onDateChange(normalizeToDayStart(date));
            }}
            disabled={
              showCalendarAvailability
                ? disableDate
                : (date) =>
                    isBeforeToday(date) || (disableDate ? disableDate(date) : false)
            }
            className="rounded-md border"
          />
          {dateLabelRenderer ? dateLabelRenderer(selectedDay) : null}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--stone-500)]">Verfügbare Zeiten</h2>

        {isLoading ? (
          <div className="rounded-lg border border-[var(--stone-200)] p-4 text-sm text-[var(--stone-600)] flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {busyLabel}
          </div>
        ) : (
          <>
            <div className={`grid grid-cols-3 sm:grid-cols-4 gap-2 ${slotGridClassName ?? ""}`}>
              {availableSlots.map((slot) => (
                <button
                  key={slot.timestamp}
                  type="button"
                  onClick={() => onSlotSelect(slot.timestamp)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    selectedSlot === slot.timestamp
                      ? "border-[var(--sage-600)] bg-[var(--sage-600)] text-white"
                      : "border-[var(--stone-200)] bg-white text-[var(--stone-700)] hover:border-[var(--sage-300)]"
                  }`}
                >
                  {slot.time}
                </button>
              ))}
            </div>

            {availableSlots.length === 0 && (
              <div className="rounded-xl border border-[var(--stone-200)] bg-[var(--stone-50)] p-4 text-sm text-[var(--stone-600)]">
                {emptyLabel}
              </div>
            )}
          </>
        )}
      </div>

      <Button
        size="client"
        className="w-full mt-4"
        onClick={onConfirm}
        disabled={!selectedSlot || isLoading}
      >
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {confirmLabel}
      </Button>
    </div>
  );
}
